import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setStoredToken, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
    const [meta, setMeta] = useState(null);

    const refresh = useCallback(async () => {
        // Skip /auth/me when no token is stored yet (avoids noisy 401 on first load)
        if (!localStorage.getItem("zentra_token")) {
            setUser(false);
            return null;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch (e) {
            setStoredToken(null);
            setUser(false);
            return null;
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        // load meta once user is known
        if (user && !meta) {
            api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
        }
    }, [user, meta]);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        if (data?.access_token) setStoredToken(data.access_token);
        setUser(data.user);
        return data.user;
    }, []);

    const register = useCallback(async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        if (data?.access_token) setStoredToken(data.access_token);
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            /* ignore */
        }
        setStoredToken(null);
        setUser(false);
    }, []);

    return (
        <AuthContext.Provider value={{ user, meta, login, register, logout, refresh, formatApiError }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
